import { useState } from "react";
import { DatabaseBackup } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateBackup } from "@/features/backups/hooks/useBackupsMutations";
import {
  BACKUP_TYPES,
  BACKUP_TYPE_LABELS,
  type BackupType,
} from "@/types/backup";

export function CreateBackupDialog() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<BackupType>("DATABASE");
  const [notes, setNotes] = useState("");
  const createMutation = useCreateBackup();
  const isPending = createMutation.isPending;

  const reset = () => {
    setType("DATABASE");
    setNotes("");
  };

  async function onSubmit() {
    try {
      await createMutation.mutateAsync({
        type,
        notes: notes.trim() || undefined,
      });
      reset();
      setOpen(false);
    } catch {
      /* mutation surfaces the error via onError */
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="default">
          <DatabaseBackup className="w-4 h-4 ml-2" />
          إنشاء نسخة احتياطية
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle>إنشاء نسخة احتياطية جديدة</DialogTitle>
          <DialogDescription>
            اختر نطاق النسخة الاحتياطية. سيبدأ إنشاء الملف في الخلفية وتتحدث حالته
            تلقائياً في القائمة.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>النوع</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as BackupType)}
              disabled={isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BACKUP_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {BACKUP_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>ملاحظات (اختياري)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="سبب النسخة الاحتياطية أو أي تفاصيل"
              disabled={isPending}
              rows={3}
            />
          </div>

          <Button
            type="button"
            onClick={onSubmit}
            disabled={isPending}
            className="w-full"
          >
            بدء الإنشاء
            {isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
